import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import { useSuppressionListsCRUDActions } from '@mm/state/actions/SuppressionListsCRUDActions';
import SuppressionSettingsController from '@mm/state/controllers/SuppressionSettingsController';
import {
  CaptureActionEnum,
  SuppressionSettingsCSVModel,
} from '@mm/state/model/SuppressionSettingsModel';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { errorToast } from '../../../../../../../../common/helpers/toast';

export const useSuppressions = (shouldInit?: boolean) => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const { retrieveAll: retriveAllSuppressionLists } =
    useSuppressionListsCRUDActions();
  const googleSheetsCRUDActions = useGoogleSheetsCRUDActions();

  const [oauthUrl, setOauthUrl] = useState('');

  const mapCSVFiles = (uploadedCSVFiles: SuppressionSettingsCSVModel[] = []) =>
    uploadedCSVFiles.map(v => ({
      id: v.id,
      name: v.fileName,
      type: v.kind,
      count: v.recordsCount,
      refreshedAt: v.refreshedAt,
      processed: v.processed,
      captureAction: v.captureAction || CaptureActionEnum.do_not_send,
    }));

  const fetchCsvFiles = async (
    captureAction: CaptureActionEnum,
    kind: 'google_sheet' | 'csv_file',
  ) => {
    const files = await SuppressionSettingsController.getUploadedCsvFiles({
      facebookPageId,
      captureAction,
      kind,
    });
    return files;
  };

  const fetchGoogleAccounts = async () => {
    if (facebookPageId) {
      await googleSheetsCRUDActions.getAccounts(facebookPageId);
    }
  };

  const fetchOauthGoogleUrl = async () => {
    if (facebookPageId) {
      const link = await googleSheetsCRUDActions.getOauthUrl(
        window.location.href,
      );
      if (link) {
        setOauthUrl(link);
      }
    }
  };

  const fetchSuppressionLists = async () => {
    if (facebookPageId) {
      await retriveAllSuppressionLists(facebookPageId);
    }
  };

  const init = async () => {
    try {
      await Promise.all([
        fetchGoogleAccounts(),
        fetchOauthGoogleUrl(),
        fetchSuppressionLists(),
      ]);
    } catch (err) {
      errorToast('Error loading contact suppression settings', 'top-right');
    }
  };

  useEffect(() => {
    if (shouldInit) {
      init();
    }
  }, []);

  return {
    fetchCsvFiles,
    mapCSVFiles,
    fetchGoogleAccounts,
    fetchOauthGoogleUrl,
    fetchSuppressionLists,
    oauthUrl,
  };
};
